/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";

import { Td, Tr } from "@lightspeed/cirrus-table";

import { Button } from "@lightspeed/flame/Button";
import { Input } from "@lightspeed/flame/Input";

import { useDispatch, useSelector } from "react-redux";
import * as databaseActions from "../../../redux/actions/databaseActions";
import D365DimensionsModal from "../D365DimensionsModal";
import {
  formatD365DimensionTooltip,
  getD365DimensionMappingValue,
  hasD365DimensionMapping,
} from "../../../utils/d365Dimensions";

const Row = ({
  item,
  shopShow,
  d365DimensionsList = [],
  isD365Integration = false,
}) => {
  const dispatch = useDispatch();
  const ui = useSelector((state) => state.ui);
  const userState = useSelector((state) => state.userState);
  const database = useSelector((state) => state.database);
  const [edit, setEdit] = useState(false);
  const [changed, setChanged] = useState(false);

  const [inputBox1, setInputBox1] = useState("");
  const [inputBox2, setInputBox2] = useState("");
  const [inputBox3, setInputBox3] = useState("");
  const [inputBox4, setInputBox4] = useState("");
  const [inputBox5, setInputBox5] = useState("");
  const [inputBox6, setInputBox6] = useState("");
  const [mealMappings, setMealMappings] = useState([]);
  const [dimensionMapping, setDimensionMapping] = useState("");
  const [d365Modal, setD365Modal] = useState(false);

  const normalAccount = item.code === null ? "" : item.code;
  const normalAccountDescription = item.name === null ? "" : item.name;
  const normalAccount2 = item.code2 === null ? "" : item.code2;
  const normalAccountDescription2 = item.name2 === null ? "" : item.name2;

  const ClassDebit = item.class1 === null ? "" : item.class1;
  const ClassCredit = item.class2 === null ? "" : item.class2;
  const normalDimensionMapping = getD365DimensionMappingValue(item);
  const mealPeriodSettings = ui.settings || {};
  const activeMealPeriods =
    database.currentCompany.type === "RESTOK" &&
    mealPeriodSettings.meal_period_enabled === "on"
      ? [1, 2, 3, 4]
          .map((index) => ({
            index,
            label: String(mealPeriodSettings[`meal_period_${index}_name`] || "").trim(),
          }))
          .filter((period) => period.label !== "")
      : [];
  const isMealPeriodMode = activeMealPeriods.length > 0;

  const parseMaybeJson = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;

    try {
      return JSON.parse(value);
    } catch (err) {
      return null;
    }
  };

  const getExistingMealMapping = (period) => {
    const rawMappings = parseMaybeJson(
      item?.meal_period_mapping || item?.meal_period_mappings || item?.meal_periods
    );
    const mappingList = Array.isArray(rawMappings)
      ? rawMappings
      : Array.isArray(rawMappings?.mappings)
      ? rawMappings.mappings
      : [];

    return (
      mappingList.find(
        (mapping) =>
          Number(
            mapping.key ??
              mapping.meal_period_key ??
              mapping.index ??
              mapping.meal_period_index ??
              mapping.period_index
          ) === period.index ||
          mapping.name === period.label ||
          mapping.meal_period_name === period.label
      ) || {}
    );
  };

  const buildMealMappings = () =>
    activeMealPeriods.map((period) => {
      const existing = getExistingMealMapping(period);

      return {
        key: period.index,
        mealPeriodKey: period.index,
        rowKey: `${item.shop_id ?? database.currentShop?.area_id ?? 0}-${item.what}-${period.index}`,
        index: period.index,
        name: period.label,
        code:
          existing.code ?? existing.token_code ?? normalAccount,
        codeName:
          existing.name ??
          existing.code_name ??
          existing.token_code_name ??
          normalAccountDescription,
        code2:
          existing.code2 ?? existing.token_code2 ?? normalAccount2,
        codeName2:
          existing.name2 ??
          existing.code_name2 ??
          existing.token_code_name2 ??
          normalAccountDescription2,
        class1:
          existing.class1 ?? existing.class_debit ?? ClassDebit,
        class2:
          existing.class2 ?? existing.class_credit ?? ClassCredit,
      };
    });

  useEffect(() => {
    setInputBox1(normalAccount);
    setInputBox2(normalAccountDescription);
    setInputBox3(normalAccount2);
    setInputBox4(normalAccountDescription2);
    setInputBox5(ClassDebit);
    setInputBox6(ClassCredit);
    setMealMappings(buildMealMappings());
    setDimensionMapping(normalDimensionMapping);

    setEdit(false);

    // kad se promijeni item iz baze -> nema promjena
    setChanged(false);
  }, [
    normalAccount,
    normalAccountDescription,
    normalAccount2,
    normalAccountDescription2,
    ClassDebit,
    ClassCredit,
    normalDimensionMapping,
    item.meal_period_mapping,
    item.meal_period_mappings,
    item.meal_periods,
    mealPeriodSettings.meal_period_enabled,
    mealPeriodSettings.meal_period_1_name,
    mealPeriodSettings.meal_period_2_name,
    mealPeriodSettings.meal_period_3_name,
    mealPeriodSettings.meal_period_4_name,
  ]);

  useEffect(() => {
    const initialMealMappings = buildMealMappings();
    const mealChanges =
      isMealPeriodMode &&
      JSON.stringify(mealMappings) !== JSON.stringify(initialMealMappings);

    setChanged((prev) => prev || mealChanges);
  }, [
    isMealPeriodMode,
    mealMappings,
    mealPeriodSettings.meal_period_enabled,
    mealPeriodSettings.meal_period_1_name,
    mealPeriodSettings.meal_period_2_name,
    mealPeriodSettings.meal_period_3_name,
    mealPeriodSettings.meal_period_4_name,
  ]);

  const updateMealMapping = (periodIndex, changes) => {
    setMealMappings((prev) =>
      prev.map((mapping) =>
        mapping.index === periodIndex ? { ...mapping, ...changes } : mapping
      )
    );
    setChanged(true);
  };

  const getMealMappingsPayload = () =>
    mealMappings.map((mapping) => ({
      key: mapping.mealPeriodKey,
      meal_period_key: mapping.mealPeriodKey,
      meal_period_index: mapping.index,
      meal_period_name: mapping.name,
      code: mapping.code,
      name: mapping.codeName,
      code_name: mapping.codeName,
      code2: mapping.code2,
      name2: mapping.codeName2,
      code_name2: mapping.codeName2,
      class1: mapping.class1,
      class2: mapping.class2,
    }));

  const handleUpdate = (dimensionMappingOverride) => {
    const d365DimensionsValue =
      typeof dimensionMappingOverride === "string"
        ? dimensionMappingOverride
        : currentDimensionMapping || "";
    const primaryMealMapping = isMealPeriodMode
      ? mealMappings.find((mapping) => mapping.index === 1)
      : null;
    const otherCode = primaryMealMapping?.code ?? inputBox1;
    const otherCodeName = primaryMealMapping?.codeName ?? inputBox2;
    const otherCode2 = primaryMealMapping?.code2 ?? inputBox3;
    const otherCodeName2 = primaryMealMapping?.codeName2 ?? inputBox4;
    const otherClass1 = primaryMealMapping?.class1 ?? inputBox5;
    const otherClass2 = primaryMealMapping?.class2 ?? inputBox6;

    if (database.currentCompany.type === "RETAIL") {
      dispatch(
        databaseActions.updateRetailOther(
          database.currentCompany.id,
          userState.user.user_id,
          database.currentShop.shop_id,
          item.what,
          inputBox1,
          inputBox2,
          inputBox3,
          inputBox4,
          inputBox5,
          inputBox6,
          d365DimensionsValue,
          databaseActions.getShopCategoriesOther(
            database.currentCompany.id,
            userState.user.user_id,
            database.currentShop.shop_id
          )
        )
      );
    }

    if (database.currentCompany.type === "RETAILX") {
      dispatch(
        databaseActions.updateRetailOtherX(
          database.currentCompany.id,
          userState.user.user_id,
          database.currentShop.shop_id,
          item.what,
          inputBox1,
          inputBox2,
          inputBox5,
          inputBox6,
          databaseActions.getShopCategoriesOtherX(
            database.currentCompany.id,
            userState.user.user_id,
            database.currentShop.shop_id
          )
        )
      );
    }

    if (database.currentCompany.type === "RESTOK") {
      dispatch(
        databaseActions.updateRestoOtherK(
          database.currentCompany.id,
          database.currentShop.area_id,
          item.what,
          otherCode,
          otherCodeName,
          otherCode2,
          otherCodeName2,
          otherClass1,
          otherClass2,
          isMealPeriodMode ? getMealMappingsPayload() : null,
          d365DimensionsValue,
          databaseActions.getAreaCategoriesOtherK(
            userState.user.user_id,
            database.currentCompany.id,
            database.currentShop.area_id
          )
        )
      );
    }

    if (database.currentCompany.type === "RESTO") {
      dispatch(
        databaseActions.updateRestoOther(
          database.currentCompany.id,
          database.currentShop.area_id,
          item.what,
          inputBox1,
          inputBox2,
          inputBox3,
          inputBox4,
          inputBox5,
          inputBox6,
          databaseActions.getAreaCategoriesOther(
            userState.user.user_id,
            database.currentCompany.id,
            database.currentShop.area_id
          )
        )
      );
    }

    setEdit(false);
    setChanged(false); // nakon spremanja više nema promjena
  };

  const currentDimensionMapping = dimensionMapping || normalDimensionMapping;

  const renderD365SettingsButton = () =>
    isD365Integration ? (
      <button
        type="button"
        style={{
          border: "1px solid #cbd5e1",
          borderRadius: 6,
          background: "#fff",
          color: "#0f172a",
          cursor: "pointer",
          fontSize: 12,
          padding: "3px 6px",
        }}
        title={formatD365DimensionTooltip(currentDimensionMapping) || "No dimensions mapped"}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setD365Modal(true);
        }}
      >
        {hasD365DimensionMapping(currentDimensionMapping) ? "Dimensions*" : "Dimensions"}
      </button>
    ) : null;

  const applyD365DimensionValue = (nextValue) => {
    handleUpdate(nextValue);
  };

  const d365ModalElement = (
    <D365DimensionsModal
      open={d365Modal}
      value={currentDimensionMapping}
      dimensions={(d365DimensionsList || []).filter((row) => row.dimension_level)}
      onClose={() => setD365Modal(false)}
      onApply={applyD365DimensionValue}
    />
  );

  if (isMealPeriodMode) {
    const rows = mealMappings.length > 0 ? mealMappings : buildMealMappings();

    if (!edit) {
      return (
        <>
          {d365ModalElement}
          {rows.map((mapping, index) => (
            <Tr
              key={`${mapping.rowKey}-${database.currentShop?.area_id ?? 0}`}
              textAlign="right"
              className={`${ui.theme === "flame" ? "" : "dark-table-row"}`}
              onDoubleClick={() => setEdit(true)}
            >
              {index === 0 && (
                <>
                  <Td width="15%" rowSpan={rows.length}>
                    {database.currentCompany.id}
                  </Td>
                  {shopShow && (
                    <Td width="15%" rowSpan={rows.length}>
                      {item.shop_id}
                    </Td>
                  )}
                </>
              )}

              <Td width="14%">
                <div style={{ display: "grid", gridTemplateColumns: "12ch 1fr", alignItems: "center" }}>
                  <strong title={mapping.name} style={{ display: "block", maxWidth: "12ch", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {mapping.name}
                  </strong>
                  <span>{mapping.code}</span>
                </div>
              </Td>
              <Td width="20%">{mapping.codeName}</Td>
              <Td width="14%">{mapping.code2}</Td>
              <Td width="20%">{mapping.codeName2}</Td>
              <Td width="20%">{mapping.class1}</Td>
              <Td width="20%">{mapping.class2}</Td>

              {index === 0 && (
                <>
                  {isD365Integration && (
                    <Td width="12%" rowSpan={rows.length}>
                      {renderD365SettingsButton()}
                    </Td>
                  )}
                  <Td width="10%" textAlign="right" rowSpan={rows.length}>
                    <Button onClick={() => setEdit(true)} size="small" fill="true">
                      Edit
                    </Button>
                  </Td>
                </>
              )}
            </Tr>
          ))}
        </>
      );
    }

    return (
      <>
        {d365ModalElement}
        {rows.map((mapping, index) => (
          <Tr
            key={`${mapping.rowKey}-${database.currentShop?.area_id ?? 0}`}
            textAlign="right"
            className={`${ui.theme === "flame" ? "" : "dark-table-row"}`}
            onDoubleClick={() => setEdit(true)}
          >
            {index === 0 && (
              <>
                <Td width="15%" rowSpan={rows.length}>
                  {database.currentCompany.id}
                </Td>
                {shopShow && (
                  <Td width="15%" rowSpan={rows.length}>
                    {item.shop_id}
                  </Td>
                )}
              </>
            )}

            <Td width="14%">
              <div style={{ display: "grid", gridTemplateColumns: "12ch 1fr", alignItems: "center" }}>
                <strong title={mapping.name} style={{ display: "block", maxWidth: "12ch", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {mapping.name}
                </strong>
                <Input
                  className="input-wrapper-row-inputbox"
                  type="text"
                  size="small"
                  value={mapping.code}
                  onChange={(e) => updateMealMapping(mapping.index, { code: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleUpdate();
                  }}
                />
              </div>
            </Td>

            <Td width="20%">
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={mapping.codeName}
                onChange={(e) => updateMealMapping(mapping.index, { codeName: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                }}
              />
            </Td>

            <Td width="14%">
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={mapping.code2}
                onChange={(e) => updateMealMapping(mapping.index, { code2: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                }}
              />
            </Td>

            <Td width="20%">
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={mapping.codeName2}
                onChange={(e) => updateMealMapping(mapping.index, { codeName2: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                }}
              />
            </Td>

            <Td width="20%">
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={mapping.class1}
                onChange={(e) => updateMealMapping(mapping.index, { class1: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                }}
              />
            </Td>

            <Td width="20%">
              <Input
                className="input-wrapper-row-inputbox"
                type="text"
                size="small"
                value={mapping.class2}
                onChange={(e) => updateMealMapping(mapping.index, { class2: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleUpdate();
                }}
              />
            </Td>

            {index === 0 && (
              <>
                {isD365Integration && (
                  <Td width="12%" rowSpan={rows.length}>
                    {renderD365SettingsButton()}
                  </Td>
                )}
                <Td width="10%" textAlign="right" rowSpan={rows.length}>
                  {!edit ? (
                    <Button onClick={() => setEdit(true)} size="small" fill="true">
                      Edit
                    </Button>
                  ) : (
                    <Button onClick={handleUpdate} size="small" fill="true" disabled={!changed}>
                      Update
                    </Button>
                  )}
                </Td>
              </>
            )}
          </Tr>
        ))}
      </>
    );
  }

  return (
    <>
    {d365ModalElement}
    <Tr
      textAlign="right"
      className={`${ui.theme === "flame" ? "" : "dark-table-row"}`}
      onDoubleClick={() => {
        setInputBox1(normalAccount);
        setInputBox2(normalAccountDescription);
        setInputBox3(normalAccount2);
        setInputBox4(normalAccountDescription2);
        setInputBox5(ClassDebit);
        setInputBox6(ClassCredit);
        setMealMappings(buildMealMappings());
        setEdit(true);
      }}
    >
      <Td width="15%">{database.currentCompany.id}</Td>
      {shopShow && <Td width="15%">{item.shop_id}</Td>}

      {/* 1 */}
      <Td width="14%">
        {!edit && (
          <div className="row-input-wrapper">
            {inputBox1}
            <div className="flex" />
          </div>
        )}
        {edit && (
          <div className="row-input-wrapper">
            <Input
              className="input-wrapper-row-inputbox"
              type="text"
              size="small"
              value={inputBox1}
              onChange={(e) => {
                setInputBox1(e.target.value);
                setChanged(true); // 👈 OVO
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUpdate();
              }}
            />
          </div>
        )}
      </Td>

      {/* 2 */}
      <Td width="20%">
        {!edit && (
          <div className="row-input-wrapper">
            {inputBox2}
            <div className="flex" />
          </div>
        )}
        {edit && (
          <div className="row-input-wrapper">
            <Input
              className="input-wrapper-row-inputbox"
              type="text"
              size="small"
              value={inputBox2}
              onChange={(e) => {
                setInputBox2(e.target.value);
                setChanged(true); // 👈
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUpdate();
              }}
            />
          </div>
        )}
      </Td>

      {/* 3 */}
      <Td width="14%">
        {!edit && (
          <div className="row-input-wrapper">
            {inputBox3}
            <div className="flex" />
          </div>
        )}
        {edit && (
          <div className="row-input-wrapper">
            <Input
              className="input-wrapper-row-inputbox"
              type="text"
              size="small"
              value={inputBox3}
              onChange={(e) => {
                setInputBox3(e.target.value);
                setChanged(true); // 👈
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUpdate();
              }}
            />
          </div>
        )}
      </Td>

      {/* 4 */}
      <Td width="20%">
        {!edit && (
          <div className="row-input-wrapper">
            {inputBox4}
            <div className="flex" />
          </div>
        )}
        {edit && (
          <div className="row-input-wrapper">
            <Input
              className="input-wrapper-row-inputbox"
              type="text"
              size="small"
              value={inputBox4}
              onChange={(e) => {
                setInputBox4(e.target.value);
                setChanged(true); // 👈
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUpdate();
              }}
            />
          </div>
        )}
      </Td>

      {/* 5 */}
      <Td width="20%">
        {!edit && (
          <div className="row-input-wrapper">
            {inputBox5}
            <div className="flex" />
          </div>
        )}
        {edit && (
          <div className="row-input-wrapper">
            <Input
              className="input-wrapper-row-inputbox"
              type="text"
              size="small"
              value={inputBox5}
              onChange={(e) => {
                setInputBox5(e.target.value);
                setChanged(true); // 👈
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUpdate();
              }}
            />
          </div>
        )}
      </Td>

      {/* 6 */}
      <Td width="20%">
        {!edit && (
          <div className="row-input-wrapper">
            {inputBox6}
            <div className="flex" />
          </div>
        )}
        {edit && (
          <div className="row-input-wrapper">
            <Input
              className="input-wrapper-row-inputbox"
              type="text"
              size="small"
              value={inputBox6}
              onChange={(e) => {
                setInputBox6(e.target.value);
                setChanged(true); // 👈
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleUpdate();
              }}
            />
          </div>
        )}
      </Td>

      {isD365Integration && <Td width="12%">{renderD365SettingsButton()}</Td>}

      <Td width="10%" textAlign="right">
        {!edit && (
          <div className="row-input-wrapper-end">
            <Button
              onClick={(_) => {
                setInputBox1(normalAccount);
                setInputBox2(normalAccountDescription);
                setInputBox3(normalAccount2);
                setInputBox4(normalAccountDescription2);
                setInputBox5(ClassDebit);
                setInputBox6(ClassCredit);
                setMealMappings(buildMealMappings());
                setEdit(true);
              }}
              size="small"
              fill="true"
            >
              Edit
            </Button>
          </div>
        )}

        {edit && (
          <div className="row-input-wrapper-end">
            {changed ? (
              <Button onClick={handleUpdate} size="small" fill="true">
                Update
              </Button>
            ) : inputBox1 === "" && inputBox2 === "" ? null : (
              <Button
                onClick={(_) => {
                  setInputBox1(normalAccount);
                  setInputBox2(normalAccountDescription);
                  setInputBox3(normalAccount2);
                  setInputBox4(normalAccountDescription2);
                  setInputBox5(ClassDebit);
                  setInputBox6(ClassCredit);
                  setMealMappings(buildMealMappings());
                  setEdit(false);
                  setChanged(false);
                }}
                size="small"
                fill="true"
              >
                Cancel
              </Button>
            )}
          </div>
        )}
      </Td>
    </Tr>
    </>
  );
};

export default Row;

